import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  consumeAskMooOpenOnMount,
  currentLocationPath,
  requestAskMooOpenOnMount,
} from './studio-return';

/**
 * Putting the browser back where a per-user OAuth flow started.
 *
 * chat-agent signs a return path into the OAuth state and redirects straight
 * to it, so this module is not the primary mechanism. It covers the two things
 * that server-side redirect cannot:
 *
 *  - The chat panel is not a route. It is an overlay `ConsoleLayout` mounts, so
 *    landing on the right URL still leaves it closed — reopening it is a
 *    console-side concern, handled by `requestAskMooOpenOnMount`.
 *  - The console can ship before chat-agent does. Until it deploys, the
 *    callback still lands on Moo Studio's My Apps page regardless of what was
 *    sent, and the recorded path is how that page knows to forward the user on.
 *
 * Per-tab and read-once, like `studio-return`. `sessionStorage` can throw
 * (Safari private mode, quota, group policy), so every access degrades to "no
 * recorded path" — which is just the old always-Moo-Studio behaviour.
 */

const RETURN_PATH_KEY = 'moo.connect.returnPath';

/** Mirrors chat-agent's `CONSOLE_CONNECTED_APPS_PATH`. */
const CONNECTED_APPS_PATH = '/moo/studio/connected-apps';

/**
 * Slightly longer than the OAuth state's own 10-minute TTL. Once chat-agent
 * honours the return path nothing consumes this marker on the happy path, so
 * without an expiry it would linger for the life of the tab and could
 * misdirect an unrelated visit to My Apps much later.
 */
const MAX_AGE_MS = 15 * 60 * 1000;

/** The outcome params chat-agent appends, carried across a fallback bounce. */
const OUTCOME_PARAMS = ['connected', 'connect_error'] as const;

function rememberConnectReturnPath(path: string): void {
  try {
    sessionStorage.setItem(RETURN_PATH_KEY, `${Date.now()}|${path}`);
  } catch {
    // disabled / quota — degrade silently
  }
}

/** Reads and clears the path recorded by {@link beginConnectReturn}. */
function takeConnectReturnPath(): string | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(RETURN_PATH_KEY);
    sessionStorage.removeItem(RETURN_PATH_KEY);
  } catch {
    return null;
  }
  if (!raw) {
    return null;
  }
  const separator = raw.indexOf('|');
  const storedAt = Number(raw.slice(0, separator));
  const path = raw.slice(separator + 1);
  if (
    !path ||
    !Number.isFinite(storedAt) ||
    Date.now() - storedAt > MAX_AGE_MS
  ) {
    return null;
  }
  return path;
}

function clearConnectReturn(): void {
  try {
    sessionStorage.removeItem(RETURN_PATH_KEY);
  } catch {
    // disabled / quota — degrade silently
  }
  // Read-and-clear is the only way to unset it, and there is nothing to act on.
  consumeAskMooOpenOnMount();
}

/**
 * Counts every attempt, process-wide. The markers are shared session state, but
 * `useStartConnectionAuthorize` is instantiated per Connect button — one per
 * app card, plus the composer chip. "Is this still my attempt?" therefore
 * cannot be answered from a hook-local ref: a card whose authorize failed would
 * clear markers a second card had just written, and that second flow would come
 * back to Moo Studio with the panel shut.
 */
let latestAttempt = 0;

export interface ConnectReturn {
  /** The path to hand chat-agent as `return_path`. */
  readonly returnPath: string;
  /**
   * Undoes this attempt's markers — for a user who pressed Connect and then
   * backed out, or whose authorize call failed. Without it the pending "reopen
   * the panel" flag survives until something consumes it, and the panel springs
   * open on a later page load the user never connected anything on.
   *
   * A no-op once a later attempt has recorded its own markers: those belong to
   * a flow that is still live, and this one has no claim on them.
   */
  readonly abandon: () => void;
}

/**
 * Records where the user is at the moment they press Connect.
 *
 * Called from the click rather than from the mutation's `onSuccess`: by then
 * the location is unchanged, but "where they were when they asked" is a fact
 * about the start of the flow, not about when the POST happened to land.
 */
export function beginConnectReturn(): ConnectReturn {
  latestAttempt += 1;
  const attempt = latestAttempt;

  const path = currentLocationPath() || CONNECTED_APPS_PATH;
  rememberConnectReturnPath(path);
  // Moo Studio renders outside ConsoleLayout, so there is no panel there to
  // reopen. Anywhere else, the only ways to reach Connect are inside the panel
  // (the composer chip, the settings modal, an in-chat prompt card), so coming
  // back with it closed would strand the user mid-conversation.
  if (!path.startsWith('/moo/')) {
    requestAskMooOpenOnMount();
  }

  return {
    returnPath: path,
    abandon: () => {
      if (attempt !== latestAttempt) {
        return;
      }
      clearConnectReturn();
    },
  };
}

/** Re-attaches `?connected` / `?connect_error` to the path being bounced to. */
function withOutcome(returnPath: string, search: string): string {
  const landed = new URLSearchParams(search);
  const carried = new URLSearchParams();
  OUTCOME_PARAMS.forEach((key) => {
    const value = landed.get(key);
    if (value !== null) {
      carried.set(key, value);
    }
  });
  const query = carried.toString();
  if (!query) {
    return returnPath;
  }
  return `${returnPath}${returnPath.includes('?') ? '&' : '?'}${query}`;
}

/**
 * Forwards a user whom chat-agent dropped on My Apps to where they actually
 * started. A no-op once chat-agent honours the return path itself, and a no-op
 * for anyone who simply navigated to My Apps: it only acts on a URL carrying
 * the callback's own outcome params.
 */
export function useConnectReturnBounce(): void {
  const history = useHistory();
  const { pathname, search } = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(search);
    if (!OUTCOME_PARAMS.some((key) => params.has(key))) {
      return;
    }
    const returnPath = takeConnectReturnPath();
    if (!returnPath) {
      return;
    }
    const target = withOutcome(returnPath, search);
    // chat-agent already put us where we asked to be.
    if (target === `${pathname}${search}`) {
      return;
    }
    // `replace`, not `push`: the OAuth callback should not leave a Back-button
    // step that re-enters this same redirect.
    history.replace(target);
  }, [history, pathname, search]);
}
